import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { AdminShell } from "@/components/Layout";
import BrandedHero, { FloatingCardsArea } from "@/components/BrandedHero";
import StatusFlag from "@/components/StatusFlag";
import {
  House, Broadcast, Plus, ArrowsClockwise, X, CheckCircle,
  Clock, Sparkle, Storefront, StackSimple, CaretDown, ArrowRight,
} from "@phosphor-icons/react";

/**
 * AdminTurnsBoardV2 — CP-style vacant turns board with BrandedHero, floating
 * KPI cards, and modern column cards. Same behavior as V1 (poll every 30s,
 * release + manual add) but wrapped in the new visual system.
 */
export default function AdminTurnsBoardV2() {
  const [board, setBoard] = useState({ open: [], in_progress: [] });
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [releasing, setReleasing] = useState(null);
  const [dragId, setDragId] = useState(null);        // id being dragged
  const [dragFrom, setDragFrom] = useState(null);    // "open" | "in_progress" | "awaiting"
  const [dropHover, setDropHover] = useState(null);  // column key currently hovered

  const load = useCallback(async () => {
    try {
      const r = await api.get("/turns/board");
      setBoard(r.data || { open: [], in_progress: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const release = async (id) => {
    setReleasing(id);
    try {
      await api.post(`/turns/${id}/release`);
      toast.success("Turn released · marked complete");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not release"));
    } finally {
      setReleasing(null);
    }
  };

  // Drag-and-drop: admin moves a card between columns.
  const moveTo = async (id, target) => {
    try {
      await api.post(`/turns/${id}/move/${target}`);
      const label = target === "open" ? "Open Pool"
                  : target === "in_progress" ? "In Progress"
                  : target === "awaiting" ? "Awaiting Release"
                  : "Complete";
      toast.success(`Moved to ${label}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Move failed"));
    }
  };

  const onDragStart = (id, from) => {
    setDragId(id); setDragFrom(from);
  };
  const onDragEnd = () => {
    setDragId(null); setDragFrom(null); setDropHover(null);
  };
  const onDrop = (target) => {
    if (!dragId) return;
    // Guard — don't call API if the card didn't actually change columns
    const fromKey = dragFrom;
    const already =
      (fromKey === "open" && target === "open") ||
      (fromKey === "in_progress" && target === "in_progress") ||
      (fromKey === "awaiting" && target === "awaiting");
    if (!already) moveTo(dragId, target);
    onDragEnd();
  };

  const { awaiting, inProgActive, totalOpen } = useMemo(() => {
    const aw = (board.in_progress || []).filter(j => j.turn_awaiting_release || j.turn_finished_at);
    const ip = (board.in_progress || []).filter(j => !j.turn_awaiting_release && !j.turn_finished_at);
    return { awaiting: aw, inProgActive: ip, totalOpen: (board.open || []).length };
  }, [board]);

  return (
    <AdminShell>
      <BrandedHero
        breadcrumbs={[{ to: "/admin", label: "Home" }, { label: "Turns Board" }]}
        eyebrow="Live · vacant unit pool"
        title="Turns Board"
        subtitle="Self-serve pool for the 7 AM crew. Techs claim, finish, and drop back to office for release."
        size="lg"
        actions={
          <>
            <button onClick={load} data-testid="turns-refresh-btn"
              className="h-10 w-10 rounded-xl bg-white/15 hover:bg-white/25 backdrop-blur text-white flex items-center justify-center transition-colors">
              <ArrowsClockwise size={18} weight="bold"/>
            </button>
            <button onClick={() => setAddOpen(true)} data-testid="turns-add-btn"
              className="h-10 px-4 rounded-xl bg-white text-slate-900 hover:bg-slate-100 font-black uppercase tracking-widest text-xs flex items-center gap-1.5 shadow-lg">
              <Plus size={16} weight="bold"/> Add Turn
            </button>
          </>
        }
      />

      <FloatingCardsArea>
        <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6">
          <KpiCard label="Open Pool" value={totalOpen} tone="cyan" icon={<Storefront size={22} weight="fill"/>} testid="kpi-open"/>
          <KpiCard label="In Progress" value={inProgActive.length} tone="fuchsia" icon={<Sparkle size={22} weight="fill"/>} testid="kpi-inprog"/>
          <KpiCard label="Awaiting Release" value={awaiting.length} tone="emerald" icon={<CheckCircle size={22} weight="fill"/>} testid="kpi-awaiting"/>
        </div>

        {loading && (
          <div className="text-center py-12 text-sm font-black uppercase tracking-widest text-slate-500">
            Loading…
          </div>
        )}

        {/* 3-column layout on desktop, stacked on mobile */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <ColumnCard title="Open Pool" count={totalOpen} tone="cyan" testid="col-open"
            hint="Available for any tech to claim · drag a card here to send back"
            dropTarget="open" onDrop={onDrop} isHover={dropHover === "open"}
            onDragOver={setDropHover} onDragLeave={() => setDropHover(null)}>
            {(board.open || []).length === 0 && !loading && (
              <EmptyBlock label="No open turns" hint="Add one via the button above."/>
            )}
            {(board.open || []).map(j => (
              <TurnCard key={j.id} j={j} tone="cyan" testid={`admin-turn-open-${j.id}`}
                onMerged={load}
                onDragStart={() => onDragStart(j.id, "open")}
                onDragEnd={onDragEnd}
                isDragging={dragId === j.id}
                currentCol="open"
                onMoveTo={(target) => moveTo(j.id, target)}
                actions={
                  <Link to={`/admin/jobs/${j.id}`} data-testid={`admin-turn-open-link-${j.id}`}
                    className="h-9 px-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-[11px] font-black uppercase tracking-widest flex items-center gap-1">
                    Open WO
                  </Link>
                }/>
            ))}
          </ColumnCard>

          <ColumnCard title="In Progress" count={inProgActive.length} tone="fuchsia" testid="col-inprog"
            hint="Currently being worked · drag here to mark started"
            dropTarget="in_progress" onDrop={onDrop} isHover={dropHover === "in_progress"}
            onDragOver={setDropHover} onDragLeave={() => setDropHover(null)}>
            {inProgActive.length === 0 && !loading && <EmptyBlock label="No active crews" hint="Idle right now — quiet field."/>}
            {inProgActive.map(j => (
              <TurnCard key={j.id} j={j} tone="fuchsia" testid={`admin-turn-inprog-${j.id}`}
                onMerged={load}
                onDragStart={() => onDragStart(j.id, "in_progress")}
                onDragEnd={onDragEnd}
                isDragging={dragId === j.id}
                currentCol="in_progress"
                onMoveTo={(target) => moveTo(j.id, target)}
                subline={
                  <span className="inline-flex items-center gap-1">
                    <Clock size={11} weight="bold"/> Started by <b className="text-slate-800">{j.turn_started_by_name || "tech"}</b>
                    {j.turn_started_at ? <span>· {new Date(j.turn_started_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span> : null}
                  </span>
                }
                actions={
                  <Link to={`/admin/jobs/${j.id}`} data-testid={`admin-turn-inprog-link-${j.id}`}
                    className="h-9 px-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-[11px] font-black uppercase tracking-widest flex items-center gap-1">
                    Open WO
                  </Link>
                }/>
            ))}
          </ColumnCard>

          <ColumnCard title="Awaiting Release" count={awaiting.length} tone="emerald" testid="col-awaiting"
            hint="Tech finished — office to confirm · drag here to mark finished"
            dropTarget="awaiting" onDrop={onDrop} isHover={dropHover === "awaiting"}
            onDragOver={setDropHover} onDragLeave={() => setDropHover(null)}>
            {awaiting.length === 0 && !loading && <EmptyBlock label="Nothing to release" hint="You're all caught up."/>}
            {awaiting.map(j => (
              <TurnCard key={j.id} j={j} tone="emerald" testid={`admin-turn-awaiting-${j.id}`}
                onMerged={load}
                onDragStart={() => onDragStart(j.id, "awaiting")}
                onDragEnd={onDragEnd}
                isDragging={dragId === j.id}
                currentCol="awaiting"
                onMoveTo={(target) => moveTo(j.id, target)}
                subline={
                  <span className="inline-flex items-center gap-1">
                    <CheckCircle size={11} weight="bold"/> Finished by <b className="text-slate-800">{j.turn_started_by_name || "tech"}</b>
                    {j.turn_finished_at ? <span>· {new Date(j.turn_finished_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span> : null}
                  </span>
                }
                actions={
                  <>
                    <Link to={`/admin/jobs/${j.id}`} data-testid={`admin-turn-awaiting-link-${j.id}`}
                      className="h-9 px-3 rounded-lg bg-white border border-slate-300 hover:bg-slate-50 text-slate-900 text-[11px] font-black uppercase tracking-widest flex items-center gap-1">
                      Open WO
                    </Link>
                    <button
                      data-testid={`turns-release-btn-${j.id}`}
                      onClick={() => release(j.id)}
                      disabled={releasing === j.id}
                      className="h-9 px-3 rounded-lg bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-[11px] font-black uppercase tracking-widest flex items-center gap-1 shadow-sm">
                      <CheckCircle weight="fill" size={13}/>
                      {releasing === j.id ? "…" : "Release"}
                    </button>
                  </>
                }/>
            ))}
          </ColumnCard>
        </div>

        {/* "Mark complete" drop zone — drag any card here to close out */}
        {dragId && (
          <div
            data-testid="col-complete-dropzone"
            onDragOver={(e) => { e.preventDefault(); setDropHover("complete"); }}
            onDragLeave={() => setDropHover(null)}
            onDrop={(e) => { e.preventDefault(); onDrop("complete"); }}
            className={`mt-4 border-2 border-dashed rounded-2xl py-6 text-center text-sm font-black uppercase tracking-widest transition-colors ${
              dropHover === "complete"
                ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                : "border-slate-300 text-slate-500"
            }`}>
            Drop here to mark complete
          </div>
        )}
      </FloatingCardsArea>

      {addOpen && <AddTurnModal onClose={() => setAddOpen(false)} onCreated={() => { setAddOpen(false); load(); }}/>}
    </AdminShell>
  );
}

/* ---------------- sub-components ---------------- */

const TONES = {
  cyan:    { chip: "bg-cyan-50 text-cyan-700 border-cyan-200", ring: "ring-cyan-100", dot: "bg-cyan-500", head: "text-cyan-700" },
  fuchsia: { chip: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200", ring: "ring-fuchsia-100", dot: "bg-fuchsia-500", head: "text-fuchsia-700" },
  emerald: { chip: "bg-emerald-50 text-emerald-700 border-emerald-200", ring: "ring-emerald-100", dot: "bg-emerald-500", head: "text-emerald-700" },
};

function KpiCard({ label, value, tone = "cyan", icon, testid }) {
  const t = TONES[tone] || TONES.cyan;
  return (
    <div data-testid={testid}
      className={`bg-white rounded-2xl p-3 sm:p-4 shadow-lg ring-1 ${t.ring} border border-slate-200`}>
      <div className="flex items-center justify-between">
        <div className={`h-9 w-9 rounded-xl flex items-center justify-center ${t.chip} border`}>
          {icon}
        </div>
        <div className={`text-3xl sm:text-4xl font-black tabular-nums ${t.head}`}>{value}</div>
      </div>
      <div className="mt-1 text-[10px] sm:text-[11px] font-black uppercase tracking-widest text-slate-500">{label}</div>
    </div>
  );
}

function ColumnCard({ title, count, tone, hint, testid, children,
                     dropTarget, onDrop, isHover, onDragOver, onDragLeave }) {
  const t = TONES[tone] || TONES.cyan;
  return (
    <div data-testid={testid}
      onDragOver={dropTarget ? (e) => { e.preventDefault(); onDragOver && onDragOver(dropTarget); } : undefined}
      onDragLeave={dropTarget ? () => { onDragLeave && onDragLeave(); } : undefined}
      onDrop={dropTarget ? (e) => { e.preventDefault(); onDrop && onDrop(dropTarget); } : undefined}
      className={`bg-white rounded-2xl border shadow-sm overflow-hidden transition-colors ${
        isHover ? "border-fuchsia-500 ring-4 ring-fuchsia-200" : "border-slate-200"
      }`}>
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
        <span className={`h-2 w-2 rounded-full ${t.dot}`}/>
        <div className={`font-black text-sm uppercase tracking-widest ${t.head}`}>{title}</div>
        <span className="ml-auto text-[11px] font-black bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full">{count}</span>
      </div>
      {hint && <div className="px-4 pt-2 text-[11px] text-slate-500">{hint}</div>}
      <div className="p-3 space-y-2 max-h-[65vh] overflow-y-auto">
        {children}
      </div>
    </div>
  );
}

function TurnCard({ j, tone = "cyan", actions, subline, testid, onMerged,
                   onDragStart, onDragEnd, isDragging,
                   currentCol, onMoveTo }) {
  const t = TONES[tone] || TONES.cyan;
  const relatedCount = j.related_count || 0;
  const [moveOpen, setMoveOpen] = useState(false);

  // Column choices available for Move dropdown — exclude current column.
  const MOVE_TARGETS = [
    { key: "open",        label: "Open Pool",      tone: "cyan" },
    { key: "in_progress", label: "In Progress",    tone: "fuchsia" },
    { key: "awaiting",    label: "Awaiting Release", tone: "emerald" },
    { key: "complete",    label: "Mark Complete",  tone: "emerald" },
  ].filter(x => x.key !== currentCol);

  const merge = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm(
      `Merge ${relatedCount} duplicate turn${relatedCount === 1 ? '' : 's'} for ${j.property_address}${j.unit ? ` · ${j.unit}` : ''} into this one?\n\n` +
      `Photos, notes, and receipts from the duplicates will be copied here. The others will be hidden (not deleted).`
    )) return;
    try {
      const r = await api.post("/jobs/merge-duplicates", {
        keep_id: j.id,
        merge_ids: j.related_ids || [],
      });
      toast.success(`Merged ${r.data.merged_count} duplicate${r.data.merged_count === 1 ? '' : 's'}`);
      onMerged && onMerged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail || "Merge failed"));
    }
  };

  return (
    <div data-testid={testid}
      draggable={!!onDragStart}
      onDragStart={onDragStart ? (e) => { e.dataTransfer.effectAllowed = "move"; onDragStart(); } : undefined}
      onDragEnd={onDragEnd}
      title={onDragStart ? "Drag to another column to move this turn" : undefined}
      className={`bg-slate-50 hover:bg-white border border-slate-200 hover:${t.ring} hover:shadow-sm rounded-xl p-3 transition-all ${
        onDragStart ? "cursor-grab active:cursor-grabbing" : ""
      } ${isDragging ? "opacity-40" : ""}`}>
      <div className="flex items-start gap-2.5">
        <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${t.chip} border shrink-0`}>
          <House weight="fill" size={16}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-black text-slate-900 truncate leading-tight">{j.property_address || "—"}</div>
          <div className="text-xs text-slate-600 mt-0.5 line-clamp-2 leading-snug">{j.title}</div>
          {subline && <div className="text-[10px] font-medium text-slate-500 mt-1.5">{subline}</div>}
          <div className="flex gap-1 mt-2 flex-wrap">
            {j.status && <StatusFlag status={j.status} size="xs"/>}
            {j.source && (
              <span className="text-[9px] font-black uppercase tracking-widest bg-white border border-slate-200 px-1.5 py-0.5 rounded text-slate-500">
                {j.source}
              </span>
            )}
            {j.vendor_name && (
              <span className="text-[9px] font-black uppercase tracking-widest bg-fuchsia-50 border border-fuchsia-200 px-1.5 py-0.5 rounded text-fuchsia-700">
                {j.vendor_name}
              </span>
            )}
            {relatedCount > 0 && (
              <button onClick={merge}
                data-testid={`turn-merge-${j.id}`}
                title={`Click to merge ${relatedCount} duplicate turn${relatedCount === 1 ? '' : 's'} into this one`}
                className="text-[9px] font-black uppercase tracking-widest bg-slate-800 hover:bg-emerald-600 text-white px-1.5 py-0.5 rounded inline-flex items-center gap-1 transition-colors">
                <StackSimple weight="fill" size={10}/> Merge +{relatedCount}
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="mt-2.5 flex gap-1.5 flex-wrap items-center">
        {actions}
        {onMoveTo && (
          <div className="relative ml-auto">
            <button
              data-testid={`turn-move-btn-${j.id}`}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMoveOpen(v => !v); }}
              className="h-9 px-3 rounded-lg bg-white border border-slate-300 hover:bg-slate-50 text-slate-900 text-[11px] font-black uppercase tracking-widest flex items-center gap-1">
              Move <CaretDown weight="bold" size={11}/>
            </button>
            {moveOpen && (
              <>
                <div className="fixed inset-0 z-20" onClick={() => setMoveOpen(false)}/>
                <div
                  data-testid={`turn-move-menu-${j.id}`}
                  className="absolute right-0 bottom-full mb-1 z-30 bg-white rounded-lg shadow-xl border border-slate-200 py-1 min-w-[180px] overflow-hidden">
                  {MOVE_TARGETS.map(tgt => (
                    <button
                      key={tgt.key}
                      data-testid={`turn-move-to-${tgt.key}-${j.id}`}
                      onClick={(e) => {
                        e.preventDefault(); e.stopPropagation();
                        setMoveOpen(false);
                        onMoveTo(tgt.key);
                      }}
                      className="w-full px-3 py-2 text-left text-[12px] font-bold text-slate-800 hover:bg-slate-100 flex items-center gap-2">
                      <ArrowRight weight="bold" size={11} className="text-slate-400"/>
                      {tgt.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyBlock({ label, hint }) {
  return (
    <div className="text-center py-8">
      <div className="text-xs font-black uppercase tracking-widest text-slate-400">{label}</div>
      {hint && <div className="text-[11px] text-slate-400 mt-1">{hint}</div>}
    </div>
  );
}

function AddTurnModal({ onClose, onCreated }) {
  const [address, setAddress] = useState("");
  const [unit, setUnit] = useState("");
  const [title, setTitle] = useState("Turn / Vacant unit");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (address.trim().length < 3) { toast.error("Address required"); return; }
    setSaving(true);
    try {
      await api.post("/turns/manual", {
        property_address: address.trim(),
        unit: unit.trim() || null,
        title: title.trim() || "Turn / Vacant unit",
      });
      toast.success("Added to the pool");
      onCreated?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not add"));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4"
      data-testid="turns-add-modal">
      <div className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-emerald-500">
          <Broadcast weight="fill" size={22} className="text-white"/>
          <div className="font-black text-lg uppercase tracking-tight flex-1 text-white">
            Add Turn to Pool
          </div>
          <button onClick={onClose} data-testid="turns-add-close"
            className="text-white/90 hover:text-white h-9 w-9 flex items-center justify-center rounded-lg hover:bg-white/15">
            <X size={20}/>
          </button>
        </div>
        <div className="p-4 space-y-3">
          <Field label="Property Address">
            <input type="text" data-testid="turns-add-address" value={address} onChange={e => setAddress(e.target.value)}
              placeholder="123 Main St"
              className="w-full h-11 px-3 border border-slate-300 rounded-xl font-medium text-sm focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 focus:outline-none"/>
          </Field>
          <Field label="Unit · optional">
            <input type="text" data-testid="turns-add-unit" value={unit} onChange={e => setUnit(e.target.value)}
              placeholder="2B"
              className="w-full h-11 px-3 border border-slate-300 rounded-xl font-medium text-sm focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 focus:outline-none"/>
          </Field>
          <Field label="Title">
            <input type="text" data-testid="turns-add-title" value={title} onChange={e => setTitle(e.target.value)}
              className="w-full h-11 px-3 border border-slate-300 rounded-xl font-medium text-sm focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 focus:outline-none"/>
          </Field>
        </div>
        <div className="p-4 border-t border-slate-100 bg-slate-50">
          <button data-testid="turns-add-submit" onClick={submit} disabled={saving}
            className="w-full h-12 bg-gradient-to-r from-cyan-500 to-emerald-500 hover:opacity-95 disabled:opacity-50 text-white font-black uppercase tracking-widest text-sm rounded-xl shadow-lg transition-opacity">
            {saving ? "Adding…" : "Add to Pool"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] font-black uppercase tracking-widest text-slate-600 mb-1.5">{label}</div>
      {children}
    </div>
  );
}
